/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { GlobalConstants, dialogMap } from '../../../../feature/src/lib/constants/global-constants';
import { Observable, from, map, of, take } from 'rxjs';
import { DotNetRequestMessageService } from '@mep/web-browser-bridge';
import { FileOrFolder } from '../../../../feature/src/lib/models/fileOrFolder';
import {
  convertFileOrFolderItemsToTreeViewItems,
  convertFileOrFolderItemToTreeView,
  convertTreeViewItemToFileOrFolder,
} from '../file-folder.utils';
import { FittingTreeView } from 'libs/sysque/toolbelt/feature/src/lib/models/fitting-tree-view-vm';
import { DownloadFamiliesReturnCodes } from 'libs/sysque/toolbelt/feature/src/lib/models/download-families-return-codes';
import { ElectricalSettingsInformation, ElectricalSettingsObjectInformation } from 'libs/sysque/toolbelt/feature/src/lib/models/electrical-settings-information';
import { ElectricalSettingsStatusResult } from 'libs/sysque/toolbelt/feature/src/lib/models/electrical-settings-return';
import { ElectricalSettingsObject } from 'libs/sysque/toolbelt/feature/src/lib/models/electrical-settings-shortcuts';
import { ElectricalSettingsReturnCodes } from 'libs/sysque/toolbelt/feature/src/lib/models/electrical-settings-return-codes';
import { MenuItemProperties } from '../../../../feature/src/lib/models/menu-item-properties';
import { FittingDefaultReturnCodes } from '../../../../feature/src/lib/models/fitting-default-return-codes';
import { AddProjectReturnCodes } from '../../../../feature/src/lib/models/add-project-return-codes';
import { FittingEnableDisableReturnCodes } from 'libs/sysque/toolbelt/feature/src/lib/models/fitting-enable-disable-return-codes';
import { ProjectRecords } from 'libs/sysque/toolbelt/feature/src/lib/models/project-records';
import { AddProjectInfo } from '../../../../feature/src/lib/models/add-project-info';
import { DeleteItemReturnCodes } from 'libs/sysque/toolbelt/feature/src/lib/models/delete-item-return-codes';
import { DisplayUnit } from '@hcworkspace/sysque/toolbelt/feature';
import { SystemInfo } from 'libs/sysque/toolbelt/data-access/src/lib/domain/system-info';
import { SystemManagerExportReturnCodes } from '@hcworkspace/sysque/shared/ui';
import { Param } from 'libs/sysque/toolbelt/feature/src/lib/components/add-family-information/add-family-information-types';


@Injectable({
  providedIn: 'root',
})
export class BackendService {
  popupRoutingValue = '';

  constructor(public messageHandler: DotNetRequestMessageService) {}

  public sendPopupRouting(route: string, width: number, height: number): void {
    const values = Array.from(dialogMap.values());
    
    const keys = Array.from(dialogMap.keys());
    if (values.filter((v) => v === route).length > 0) {
      const routingKey = keys.find((key) => dialogMap.get(key) === route);
      this.popupRoutingValue = `dialog/${routingKey}`;
      console.log('Before making the request, value of the route of add fitting dialog', this.popupRoutingValue)
      from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.OnToolBeltItemClick, [this.popupRoutingValue, width, height]));
    }
  }

  public onAddProjectClick(width: number, height: number): Observable<any> {
   return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.OnAddProjectClick, [width, height]));
  }

  public onSystemManagerItemClick(route: string, width: number, height: number): Observable<any> {
    return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.OnSystemManagerItemClick, [route, width, height]));
  }

  public getRevitInformation(): Observable<DisplayUnit> {
    return from<Promise<DisplayUnit>>(this.messageHandler.sendRequestAsync(GlobalConstants.GET_REVIT_INFORMATION_METHOD_NAME));
  }

  public changeDialogHeight(height: number): Observable<any> {
    return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.ChangeHeight, [height]));
  }

  public onCloseWindow(): Observable<any> {
    return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.OnCloseWindow));
  }

  public sendClosePopupAction(): Observable<any> {
    return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.OnClosePopupAction));
  }

  public getCurateGroups(): Observable<any> {
    return from<Promise<unknown>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetCurateGroups));
  }

  public getInstallTypes(): Observable<any> {
    return from<Promise<unknown>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetInstallTypes));
  }

  public getManufacturers(): Observable<any> {
    return from<Promise<unknown>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetManufacturers));
  }

  public getProductTypes(): Observable<any> {
    return from<Promise<unknown>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetProductTypes));
  }

  public getSubProductTypes(): Observable<any> {
    return from<Promise<unknown>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetSubProductTypes));
  }

  public getMaterialTypes(): Observable<any> {
    return from<Promise<unknown>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetMaterialTypes));
  }

  public selectionExists(): Observable<boolean> {
    return from<Promise<boolean>>(this.messageHandler.sendRequestAsync(GlobalConstants.SelectionExists));
  }

  public getConfigurationLocation(): Observable<string> {
    return from<Promise<string>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetConfigurationLocation));
  }

  public getTreeViewRootDirectory(): Observable<FittingTreeView[]> {
    return from(this.messageHandler.sendRequestAsync(GlobalConstants.GetTreeViewRootDirectory)).pipe(
      map(filesAndFolders => convertFileOrFolderItemsToTreeViewItems(filesAndFolders as FileOrFolder[]))
    );
  }

  public updateTreeViewNodeOnExpansionChanged(treeViewItem: FittingTreeView): Promise<FittingTreeView> {
    return this.messageHandler.sendRequestAsync(GlobalConstants.UpdateNodeOnExpansionChanged, [
      convertTreeViewItemToFileOrFolder(treeViewItem)
    ]).then(
      response => convertFileOrFolderItemToTreeView(response as FileOrFolder)
     
    );
  }

  public downloadMissingFamilies(): Observable<boolean> {
    return from<Promise<boolean>>(this.messageHandler.sendRequestAsync(GlobalConstants.DownloadMissingFamilies));
  }

  public checkIfUnreferencedFamilies(system: string): Promise<DownloadFamiliesReturnCodes> {
    return this.messageHandler.sendRequestAsync(GlobalConstants.CheckUnreferencedFamilies, [system]);
  }

  public downloadUnreferencedFamilies(system: string): Observable<boolean> {
    return from<Promise<boolean>>(this.messageHandler.sendRequestAsync(GlobalConstants.DownloadUnreferencedFamilies, [system]));
  }

  public updateGenericFittings(): Observable<DownloadFamiliesReturnCodes>{
    return from<Promise<DownloadFamiliesReturnCodes>>(this.messageHandler.sendRequestAsync(GlobalConstants.UpdateGenericFittings));
  }

  // Apply ID
  public changeDialogDimensions(width: number, height: number): void {
    this.messageHandler.sendRequestAsync(GlobalConstants.ChangeDialogDimensions, [width, height]);
  }

  public validateSelection(): Observable<any> {
    return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.ValidateSelection));
  }

  public applyButtonClick(id: string): void {
    this.messageHandler.sendRequestAsync(GlobalConstants.OnApplyButtonClick, [id]);
  }

  // Import schedule
  public browseClick(): Observable<string> {
    return from<Promise<string>>(this.messageHandler.sendRequestAsync(GlobalConstants.OnBrowseClick));
  }

  public importScheduleClick(scheduleName: string): void {
    this.messageHandler.sendRequestAsync(GlobalConstants.OnImportClick, [scheduleName]);
  }

  // Electrical settings
  public getElectricalSystemsInformation(): Observable<ElectricalSettingsObjectInformation> {
    const result = from<Promise<ElectricalSettingsObjectInformation>>(this.messageHandler.sendRequestAsync(GlobalConstants.LoadSystem));
    return result;
  }

  public getElectricalShortcutsModifiersInformation(): Observable<ElectricalSettingsObject> {
    return from<Promise<ElectricalSettingsObject>>(this.messageHandler.sendRequestAsync(GlobalConstants.LoadSystemModifiersKeys));
  }

  public onApplyClick(
    electricalSettings: ElectricalSettingsInformation
  ): Observable<ElectricalSettingsStatusResult> {
    return from<Promise<ElectricalSettingsStatusResult>>(this.messageHandler.sendRequestAsync(GlobalConstants.OnApplyShortcutsClick, [electricalSettings]));
  }

  public onApplyPreferencesClick(electricalSettings: ElectricalSettingsInformation): void {
    this.messageHandler.sendRequestAsync(GlobalConstants.OnApplyPreferencesClick, [electricalSettings]);
  }

  public checkInvalidShortcutsOnOpening(): Observable<ElectricalSettingsReturnCodes> {
    return from<Promise<ElectricalSettingsReturnCodes>>(this.messageHandler.sendRequestAsync(GlobalConstants.CheckInvalidShortcuts));
  }

  public getProjectsList(): Observable<ProjectRecords[]> {
    return from<Promise<ProjectRecords[]>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetProjectsList));
  }

  public addProject(addProjectInfo: AddProjectInfo): Observable<AddProjectReturnCodes> {
    return from<Promise<AddProjectReturnCodes>>(this.messageHandler.sendRequestAsync(GlobalConstants.AddProject, [addProjectInfo]));
  }

  // Right click menu logic

  public setRemoveDefaultFitting(menuItemFitting: MenuItemProperties, isDefault: boolean): Promise<FittingDefaultReturnCodes> {
    return this.messageHandler.sendRequestAsync(GlobalConstants.SetRemoveDefaultFitting, [
      menuItemFitting,
      isDefault,
    ]);
  }

  public enableDisableFitting(menuItemFitting: MenuItemProperties): Promise<FittingEnableDisableReturnCodes> {
    return this.messageHandler.sendRequestAsync(GlobalConstants.EnableDisableFitting, [menuItemFitting]);
  }

  public deleteItem(menuItemProperties: MenuItemProperties): Observable<DeleteItemReturnCodes> {
    return from<Promise<DeleteItemReturnCodes>>(this.messageHandler.sendRequestAsync(GlobalConstants.DeleteItem, [menuItemProperties]));
  }

  // System Manager
  public getVisibleInvisibleSystems(): Observable<SystemInfo[]> {
    return from<Promise<SystemInfo[]>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetVisibleInvisibleSystems));
  }

  public onExportButtonClicked(selectedSystems: string[]): Observable<SystemManagerExportReturnCodes> {
    return from<Promise<SystemManagerExportReturnCodes>>(this.messageHandler.sendRequestAsync(GlobalConstants.OnExportButtonClicked, [selectedSystems]));
  }

  public readParametersFromDocument(selectedCategory: string): Observable<Param[]> {
    return from<Promise<Param[]>>(this.messageHandler.sendRequestAsync(GlobalConstants.ReadParametersFromDocument, [selectedCategory]));
  }
}
